import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { hotArticles, categories } from '@/mocks/articles';
import ArticleCard from '@/components/base/ArticleCard';

export default function CategoryPage() {
  const { slug } = useParams();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = hotArticles.filter((a) => a.categorySlug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 pb-10 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 text-center py-20">
            <h1 className="text-2xl font-heading font-bold text-foreground-900">分类不存在</h1>
            <Link to="/" className="mt-4 inline-block text-sm text-primary-500 hover:underline">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://911baoliao.com/" },
      { "@type": "ListItem", "position": 2, "name": category.name, "item": `https://911baoliao.com/category/${category.slug}` }
    ]
  };

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `911爆料网 - ${category.name}`,
    "description": category.description,
    "url": `https://911baoliao.com/category/${category.slug}`,
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": categoryArticles.slice(0, 6).map((a, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "url": `https://911baoliao.com/article/${a.slug}`,
        "name": a.title
      }))
    },
    "breadcrumb": breadcrumbSchema
  };

  return (
    <>
      <Navbar />
      <main>
        {/* Breadcrumb */}
        <div className="pt-20 pb-0 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-400 py-3" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center"></i>
              <span className="text-foreground-700 font-medium">{category.name}</span>
            </nav>
          </div>
        </div>

        {/* Category Declaration */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="max-w-2xl mx-auto text-center">
              <p className="text-xs text-foreground-400 uppercase tracking-wider mb-4">当前分类</p>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 leading-relaxed">
                {category.name}
                <br />
                <span className="text-foreground-600 font-normal text-xl md:text-2xl">{category.description}</span>
                <span className="ml-2 text-2xl">🎭</span>
              </h1>
            </div>
          </div>
        </section>

        {/* Category Hot Ranking */}
        {categoryArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">
                    同类热榜
                    <span className="text-primary-500 ml-2 text-sm font-sans font-medium">{category.name}</span>
                  </h2>
                  <p className="text-xs text-foreground-500 mt-1.5">该分类下热度最高的爆料内容</p>
                </div>
                <span className="flex-shrink-0 px-3 py-1.5 rounded-full bg-foreground-900 text-background-50 text-xs font-semibold whitespace-nowrap">
                  热门
                </span>
              </div>

              <div className="flex gap-4 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {categoryArticles.map((article, index) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="flex-shrink-0 w-[260px] sm:w-[280px] group/card cursor-pointer"
                  >
                    <div className="relative aspect-square rounded-lg overflow-hidden bg-background-200 mb-3">
                      <img
                        src={article.image}
                        alt={article.title}
                        title={`${article.title} - 911爆料网${category.name}`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover/card:scale-105"
                        loading="lazy"
                      />
                      {article.isTop && (
                        <span className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded text-xs font-bold bg-primary-500/90 text-background-50 backdrop-blur-sm">
                          爆
                        </span>
                      )}
                      <span className="absolute bottom-2.5 right-2.5 text-4xl font-heading font-bold text-background-50/80">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <div className="px-0.5">
                      <p className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover/card:text-primary-500 transition-colors">
                        {article.title}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-xs text-accent-500 font-semibold">{article.heat} 热度</span>
                      </div>
                    </div>
                  </Link>
                ))}
                <div className="flex-shrink-0 w-20"></div>
              </div>
            </div>
          </section>
        )}

        {/* Category Featured Grid */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="mb-8">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">
                分类精选
              </h2>
              <p className="text-xs text-foreground-500 mt-1.5">{category.name} · 编辑推荐</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.slice(0, 6).map((article) => (
                <ArticleCard key={article.id} article={article} variant="grid" />
              ))}
              {categoryArticles.length === 0 && (
                <div className="col-span-3 text-center py-10">
                  <p className="text-foreground-500 text-sm">暂无该分类下的内容，敬请期待</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Related Categories */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6">
              探索更多分类
            </h2>
            <div className="flex flex-wrap gap-3">
              {categories.filter((c) => c.slug !== slug).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-background-50 border border-background-200/70 text-foreground-700 text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 hover:border-primary-300 hover:text-primary-600"
                >
                  <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* SEO Structured Data */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(collectionSchema)
        }} />
      </main>
      <Footer />
    </>
  );
}